import { computed, type Ref, ref } from 'vue'
import {
    dismissAction,
    dismissDetractorAction,
    fetchCases,
    fetchDetractorActions,
    fetchFocusArea,
    fetchLeaderboard,
    fetchRecommendedActions,
    fetchResponseData,
    fetchScorecards,
    storeDetractorAction,
} from '@/api/frontlineManagerDash'
import { getPraiseGeneratorLabel } from '@/utils/frontlineManagerDash'
import { trackAmplitude } from '@/utils/amplitude'
import {
    Cases as ICases,
    GraphData,
    PraiseAction,
    RecommendedAction,
    RespondAction,
    Scorecard as IScorecard,
    SendMethod,
} from '@/entities/frontlineManagerDash'
import { CopilotDashboardFilters, ILeaderboardResult } from '@/entities'
import { filterTimeOptions, TimeOptionLabel } from '@/utils/time'
import { saveUserConfig } from '@/api/settings/account/users'

export type NotifyUser = (message: {
    severity: 'success' | 'error' | 'warn'
    summary: string
    detail: string
    life?: number // show duration in ms
}) => void

export const useFrontlineManagerDash = (notifyUser?: NotifyUser) => {
    // Filter states
    const metricFilter = ref(undefined)
    const timeFilter: Ref<TimeOptionLabel> = ref(filterTimeOptions[2])

    // Data states
    const graphData: Ref<GraphData[]> = ref([])
    const graphSummary = ref({})
    const responseSummary = ref([])
    const responseCount = ref(0)
    const promoterActions: Ref<RecommendedAction[]> = ref([])
    const detractorActions: Ref<RecommendedAction[]> = ref([])
    const tags: Ref<string[]> = ref([])
    const selectedPraise: Ref<RecommendedAction | undefined> = ref()
    const installedApps: Ref<SendMethod[]> = ref([])
    const scorecards: Ref<IScorecard[]> = ref([])
    const teamLeaderboard: Ref<ILeaderboardResult[]> = ref([])
    const locationLeaderboard: Ref<ILeaderboardResult[]> = ref([])
    const cases: Ref<ICases | undefined> = ref()
    const casesResponseCount = ref(0)
    const focusArea = ref({})
    const showPraiseForStaff = ref(false)

    // Loading states
    const loadingResponseData = ref(true)
    const loadingPromoterActions = ref(true)
    const loadingDetractorActions = ref(true)
    const loadingScorecardData = ref(true)
    const loadingTeamLeaderboard = ref(true)
    const loadingLocationLeaderboard = ref(true)
    const loadingCasesData = ref(true)
    const loadingFocusArea = ref(true)

    // Error states
    const hasErrorResponseData = ref(false)
    const hasErrorPromoterActions = ref(false)
    const hasErrorDetractorActions = ref(false)
    const hasErrorScorecardData = ref(false)
    const hasErrorTeamLeaderboard = ref(false)
    const hasErrorLocationLeaderboard = ref(false)
    const hasErrorCasesData = ref(false)
    const hasErrorFocusArea = ref(false)

    const isPageLoading = computed(() => {
        return (
            !metricFilter.value ||
            loadingResponseData.value ||
            loadingScorecardData.value ||
            loadingTeamLeaderboard.value ||
            loadingLocationLeaderboard.value ||
            loadingCasesData.value
        )
    })
    const showPraiseActionsCard = ref(true)
    const showDetractorActionsCard = ref(true)

    const welcomeMessage = (userName: string) => {
        return userName ? `Welcome back, ${userName}!` : 'Welcome back!'
    }

    const fetchPageData = async (hasTeamGoal = false) => {
        fetchResponses()
        fetchActionsPromoter()
        fetchActionsDetractor()
        fetchScorecardData()
        fetchCasesData()
        fetchTeamLeaderboard()
        fetchLocationLeaderboard()

        if (hasTeamGoal) {
            fetchFocusAreaData()
        }
    }

    const fetchResponses = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(loadingResponseData, hasErrorResponseData)
        fetchResponseData(timeFilter.value, metricFilter.value)
            .then((response) => {
                const data = response.data
                if (data) {
                    graphData.value = data.graph
                    graphSummary.value = data.graphSummary
                    responseCount.value = data.responded
                    responseSummary.value = data.responseSummary
                }
            })
            .catch((error) => {
                hasErrorResponseData.value = true
                showFetchErrorMessage(error, 'responses')
            })
            .finally(() => {
                loadingResponseData.value = false
            })
    }

    const fetchActionsPromoter = () => {
        setCardAsLoading(loadingPromoterActions, hasErrorPromoterActions)
        fetchRecommendedActions()
            .then((response) => {
                const result = response.data?.result
                promoterActions.value =
                    result?.recommendedActions?.promoterActions ?? []
                installedApps.value = result?.installedApps ?? []
                tags.value = result?.tags ?? []
                showPraiseActionsCard.value = promoterActions.value.length > 0 // Of empyu
                showPraiseForStaff.value = result?.forAskNicelyStaff ?? false
                loadingPromoterActions.value = false
            })
            .catch((error) => {
                loadingPromoterActions.value = false
                hasErrorPromoterActions.value = true
                showFetchErrorMessage(error, 'recommended praise')
            })
    }

    const fetchActionsDetractor = () => {
        setCardAsLoading(loadingDetractorActions, hasErrorDetractorActions)
        fetchDetractorActions({
            timeUnit: timeFilter.value.time_unit,
            timeValue: Number(timeFilter.value.time_value),
            questionType: metricFilter.value,
        })
            .then((response) => {
                const actionsList = response.data?.result?.actions ?? []

                if (actionsList.length <= 0) {
                    showDetractorActionsCard.value = false
                    return
                }

                detractorActions.value = actionsList
                loadingDetractorActions.value = false
            })
            .catch((error) => {
                loadingDetractorActions.value = false
                hasErrorDetractorActions.value = true
                showFetchErrorMessage(error, 'detractor actions')
            })
    }

    const fetchScorecardData = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(loadingScorecardData, hasErrorScorecardData)

        fetchScorecards({
            timeUnit: timeFilter.value.time_unit,
            timeValue: Number(timeFilter.value.time_value),
            questionType: metricFilter.value,
        })
            .then((response) => {
                if (response?.data?.success) {
                    scorecards.value = response.data?.result ?? []
                }
            })
            .catch((error) => {
                hasErrorScorecardData.value = true
                showFetchErrorMessage(error, 'scorecard')
            })
            .finally(() => {
                loadingScorecardData.value = false
            })
    }

    const fetchCasesData = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(loadingCasesData, hasErrorCasesData)
        fetchCases(timeFilter.value, metricFilter.value)
            .then((response) => {
                cases.value = response.data.result?.cases ?? undefined
                casesResponseCount.value =
                    response.data.result?.responseCount ?? undefined
            })
            .catch((error) => {
                hasErrorCasesData.value = true
                showFetchErrorMessage(error, 'cases')
            })
            .finally(() => {
                loadingCasesData.value = false
            })
    }

    const fetchTeamLeaderboard = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(loadingTeamLeaderboard, hasErrorTeamLeaderboard)
        fetchLeaderboard('team', timeFilter.value, metricFilter.value)
            .then((response) => {
                teamLeaderboard.value = response.data.result?.leaderboard ?? []
            })
            .catch((error) => {
                hasErrorTeamLeaderboard.value = true
                showFetchErrorMessage(error, 'team leaderboard')
            })
            .finally(() => {
                loadingTeamLeaderboard.value = false
            })
    }

    const fetchLocationLeaderboard = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(
            loadingLocationLeaderboard,
            hasErrorLocationLeaderboard
        )
        fetchLeaderboard('location', timeFilter.value, metricFilter.value)
            .then((response) => {
                locationLeaderboard.value =
                    response.data.result?.leaderboard ?? []
            })
            .catch((error) => {
                hasErrorLocationLeaderboard.value = true
                showFetchErrorMessage(error, 'location leaderboard')
            })
            .finally(() => {
                loadingLocationLeaderboard.value = false
            })
    }

    const fetchFocusAreaData = () => {
        if (!timeFilter.value || !metricFilter.value) {
            return
        }

        setCardAsLoading(loadingFocusArea, hasErrorFocusArea)
        fetchFocusArea(timeFilter.value, metricFilter.value)
            .then((response) => {
                focusArea.value = response.data.result ?? []
            })
            .catch((error) => {
                hasErrorFocusArea.value = true
                showFetchErrorMessage(error, 'focus area')
            })
            .finally(() => {
                loadingFocusArea.value = false
            })
    }

    const onDismissPraise = (action: PraiseAction, index: number) => {
        const list =
            action.category === 'promoter' ? promoterActions : detractorActions
        list.value.splice(index, 1)

        if (action.slug) {
            dismissAction(action.slug)
                .then(() => {
                    trackAmplitude(
                        null,
                        {
                            praiseType: getPraiseGeneratorLabel(
                                action.generator
                            ),
                        },
                        'Praise Dismissed'
                    )
                    checkIfAllPraiseHasBeenActioned()
                })
                .catch(() => {
                    list.value.splice(index, 0, action) // Restore action
                    showPopup(
                        'error',
                        'Error',
                        'There was an error dismissing this action. Please try again.'
                    )
                })
        }
    }

    const onPraiseSent = (popupMessage: string, slug: string) => {
        showPopup('success', 'Praise Sent', popupMessage ?? '')

        const index = promoterActions.value.findIndex(
            (action) => action.slug === slug
        )
        // Remove action from list
        if (index !== -1) {
            promoterActions.value.splice(index, 1)
            checkIfAllPraiseHasBeenActioned()
        }
    }

    const checkIfAllPraiseHasBeenActioned = () => {
        // Hide praise actions card after two seconds if all praises have been actioned
        if (promoterActions.value.length <= 0) {
            setTimeout(() => {
                showPraiseActionsCard.value = false
            }, 2000)
        }
    }

    const onPraiseSendFailed = (popupMessage: string) => {
        showPopup('error', 'Error', popupMessage)
    }

    const onPraiseSendWarn = (popupMessage: string) => {
        showPopup('warn', 'Warning', popupMessage)
    }

    const handleDetractorAction = (
        action: RespondAction,
        index: number,
        callback: (questionId: number) => Promise<any>
    ) => {
        detractorActions.value.splice(index, 1)

        callback(action.question_id)
            .then(() => {
                if (detractorActions.value.length <= 0) {
                    setTimeout(() => {
                        showDetractorActionsCard.value = false
                    }, 2000)
                }
            })
            .catch(() => {
                detractorActions.value.splice(index, 0, action) // Restore action
                showPopup(
                    'error',
                    'Error',
                    'There was an error processing this action. Please try again.'
                )
            })
    }

    const onStoreDetractor = (action: RespondAction, index: number) => {
        handleDetractorAction(action, index, storeDetractorAction)
    }

    const onDismissDetractor = (action: RespondAction, index: number) => {
        handleDetractorAction(action, index, dismissDetractorAction)
    }

    const setTimeFilter = (
        copilotDashboardFilters?: CopilotDashboardFilters
    ) => {
        if (
            !copilotDashboardFilters?.timeFilter?.time_unit ||
            !copilotDashboardFilters?.timeFilter?.time_value
        ) {
            return
        }

        const timeOption = filterTimeOptions.find(
            (option) =>
                option.time_unit ===
                    copilotDashboardFilters.timeFilter.time_unit &&
                option.time_value ==
                    copilotDashboardFilters.timeFilter.time_value
        )
        if (timeOption) {
            timeFilter.value = timeOption
        }
    }

    const saveTimeFilter = async (timeOption: TimeOptionLabel) => {
        await saveUserConfig({
            copilotDashboardFilters: {
                timeFilter: {
                    time_unit: timeOption.time_unit,
                    time_value: timeOption.time_value,
                },
            },
        })
    }

    const setCardAsLoading = (
        isLoading: Ref<boolean>,
        hasError: Ref<boolean>
    ) => {
        isLoading.value = true
        hasError.value = false
    }

    const showPopup = (
        severity: 'success' | 'error' | 'warn',
        summary: string,
        detail: string,
        life = 5000 // in ms
    ) => {
        if (notifyUser) {
            notifyUser({ severity, summary, detail, life })
        }
    }

    const showFetchErrorMessage = (error, dataType: string) => {
        const responseMsg = error?.response?.data?.result?.errors?.[0]?.message
        showPopup(
            'error',
            'Error',
            responseMsg
                ? `${dataType}: ${responseMsg}`
                : `There was an error loading ${dataType}`
        )
    }

    return {
        graphData,
        graphSummary,
        responseSummary,
        focusArea,
        responseCount,
        promoterActions,
        detractorActions,
        tags,
        selectedPraise,
        installedApps,
        scorecards,
        teamLeaderboard,
        locationLeaderboard,
        cases,
        casesResponseCount,
        loadingResponseData,
        loadingPromoterActions,
        loadingDetractorActions,
        loadingScorecardData,
        loadingTeamLeaderboard,
        loadingLocationLeaderboard,
        loadingCasesData,
        loadingFocusArea,
        hasErrorResponseData,
        hasErrorPromoterActions,
        hasErrorDetractorActions,
        hasErrorScorecardData,
        hasErrorTeamLeaderboard,
        hasErrorLocationLeaderboard,
        hasErrorCasesData,
        hasErrorFocusArea,
        metricFilter,
        timeFilter,
        isPageLoading,
        showDetractorActionsCard,
        showPraiseActionsCard,
        fetchPageData,
        welcomeMessage,
        fetchResponses,
        fetchActionsPromoter,
        fetchActionsDetractor,
        fetchScorecardData,
        fetchCasesData,
        fetchTeamLeaderboard,
        fetchLocationLeaderboard,
        fetchFocusAreaData,
        onDismissPraise,
        onPraiseSent,
        onPraiseSendFailed,
        onPraiseSendWarn,
        onStoreDetractor,
        onDismissDetractor,
        setTimeFilter,
        saveTimeFilter,
        showPraiseForStaff,
        showFetchErrorMessage,
        setCardAsLoading,
    }
}
